import type { RowData } from "@tanstack/react-table";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useConstant } from "@hooks/useConstant";
import type { EditContext } from "./editActions";

const createLatestEditContext = <TData extends RowData>(latest: {
    readonly current: EditContext<TData>;
}): EditContext<TData> => ({
    get instance() {
        return latest.current.instance;
    },
    get controller() {
        return latest.current.controller;
    },
});

/**
 * One context object for the table's lifetime, so memoized rows don't
 * re-render when the instance does; editors read the latest instance from it
 * when they act.
 */
const useStableEditContext = <TData extends RowData>({
    instance,
    controller,
}: EditContext<TData>): EditContext<TData> => {
    const latestRef = useLatestRef({ instance, controller });
    return useConstant(() => createLatestEditContext(latestRef));
};

/** The spreadsheet editors' context; `null` for tables without `editing`. */
export const useEditContext = <TData extends RowData>(
    input: EditContext<TData>,
): EditContext<TData> | null => {
    const context = useStableEditContext(input);
    return input.instance.isEditable ? context : null;
};

/** The list editors' context; `null` for tables without `listEditing`. */
export const useListEditContext = <TData extends RowData>(
    input: EditContext<TData>,
): EditContext<TData> | null => {
    const context = useStableEditContext(input);
    return input.instance.hasListEditing ? context : null;
};
